/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Base } from "@/shared/ui";
import { LoadingPage } from "@pages/other/LoadingPage";
import CornerPill from "@/shared/ui/components/basic/loading/CornerPill";
import { useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";

import { loadingContext, useLoading } from "./LoadingContext.store";
import { setGlobalLoadingStore, getGlobalLoadingStore } from "./LoadingContext.global";

/**
 * Renders a full-screen loading overlay that fades in/out based on the loading queue state.
 *
 * @param props - Component props with children
 * @returns The loading view element
 */
export const LoadingView: ComponentWithChildren = ({ children }) => {
  const { isLoading, showCornerPill } = useLoading((state) => state);
  const { t } = useTranslation('common');
  
  const store = useContext(loadingContext.Context);
  
  useEffect(() => {
    if (store && !getGlobalLoadingStore()) {
      setGlobalLoadingStore(store);
    }
    const splash = document.getElementById('app-splash');
    if (splash) {
      splash.style.opacity = '0';
      setTimeout(() => splash.remove(), 200);
    }
  }, [store]);

  // Safety timeout: force-dismiss loading if stuck longer than MAX_LOADING_DURATION.
  // Prevents the app from being permanently stuck due to leaked counters or edge cases.
  useEffect(() => {
    if (!isLoading) return;

    const MAX_LOADING_DURATION = 15_000;
    const timer = setTimeout(() => {
      if (!store) return;
      const state = store.getState();
      if (state.isLoading) {
        store.setState({ loadingQueue: 0, isLoading: false, loadingStartTime: null });
      }
    }, MAX_LOADING_DURATION);

    return () => clearTimeout(timer);
  }, [isLoading, store]);
  
  useEffect(() => {
    if (isLoading) {  
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isLoading]);

  return (
    <Base
      style={
        isLoading
          ? { position: "relative" }
          : { width: "100%", height: "100dvh" }
      }
    >
      {children}
      <Base
        key="global-loading-overlay"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100dvh",
          display: isLoading ? "flex" : "none",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: 'var(--surface-primary)',
          zIndex: 9999,
          pointerEvents: isLoading ? "auto" : "none",
          opacity: isLoading ? 1 : 0,
          transition: "opacity 200ms ease-out",
        }}
      >
        <LoadingPage isActive={isLoading} overlay={false} />
      </Base>
      <CornerPill visible={showCornerPill} text={t('syncing')} />
    </Base>
  );
};
